import React, { useEffect, useState } from 'react';
import { AlertTriangle, Check, Copy, KeyRound, Save } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useLocations } from '../../hooks/locations/useLocations';
import { useCreateBioAgent, useUpdateBioAgent } from '../../hooks/useBio';
import type { BioAgent } from '../../types/bio.types';

interface BioAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null — создание нового агента. */
  agent: BioAgent | null;
  /** Выданный токен показывается один раз — родитель открывает его показ. */
  onTokenIssued: (token: string, agentName: string) => void;
}

const errorMessage = (error: unknown): string => {
  const apiError = error as { message?: string } | null;
  return apiError?.message || 'Не удалось сохранить агента';
};

export function BioAgentDialog({
  open,
  onOpenChange,
  agent,
  onTokenIssued,
}: BioAgentDialogProps) {
  const isEdit = agent !== null;
  const { data: locations } = useLocations({ limit: 100 });
  const createAgent = useCreateBioAgent();
  const updateAgent = useUpdateBioAgent();
  const isSaving = createAgent.isPending || updateAgent.isPending;

  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!open) return;
    setName(agent?.name ?? '');
    setLocationId(agent?.location_id ?? '');
    setIsActive(agent?.is_active ?? true);
    createAgent.reset();
    updateAgent.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agent]);

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        await updateAgent.mutateAsync({
          id: agent.id,
          data: { name, location_id: locationId, is_active: isActive },
        });
      } else {
        const created = await createAgent.mutateAsync({
          name,
          location_id: locationId,
        });
        onTokenIssued(created.token, created.name);
      }
      onOpenChange(false);
    } catch {
      // Текст ошибки показывается ниже — диалог остаётся открытым.
    }
  };

  const error = createAgent.error || updateAgent.error;
  const canSubmit = name.trim().length > 0 && locationId.length > 0 && !isSaving;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div>
            <DialogTitle>
              {isEdit ? 'Настройки агента' : 'Новый агент объекта'}
            </DialogTitle>
            <DialogDescription>
              Агент — программа на компьютере объекта, которая связывает
              терминалы с сервером.
            </DialogDescription>
          </div>
          {!isSaving && <DialogClose onClose={() => onOpenChange(false)} />}
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="bio-agent-name">Название</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Обычно — объект или компьютер, где стоит агент
            </p>
            <Input
              id="bio-agent-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Агент главного входа"
              disabled={isSaving}
            />
          </div>

          <div>
            <Label htmlFor="bio-agent-location">Объект</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Локация задаёт часовой пояс — по нему считаются границы суток
            </p>
            <select
              id="bio-agent-location"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              disabled={isSaving}
            >
              <option value="">— выберите объект —</option>
              {(locations?.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {isEdit && (
            <div className="flex items-center justify-between border-t pt-3">
              <div className="space-y-0.5">
                <Label htmlFor="bio-agent-active">Активен</Label>
                <p className="text-xs text-muted-foreground">
                  Выключенный агент не получает команды и не принимает события
                </p>
              </div>
              <Switch
                id="bio-agent-active"
                checked={isActive}
                onCheckedChange={setIsActive}
                disabled={isSaving}
              />
            </div>
          )}

          {!isEdit && (
            <div className="flex gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-100">
              <KeyRound className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>
                После создания будет показан токен агента —{' '}
                <strong>единственный раз</strong>. Скопируйте его и передайте
                тому, кто настраивает агент на объекте.
              </span>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 rounded-md bg-red-100 p-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>{errorMessage(error)}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Отмена
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            <Save className="mr-2 h-4 w-4" />
            {isEdit ? 'Сохранить' : 'Создать агента'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BioAgentTokenDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  token: string;
  agentName: string;
}

/** Показ токена: сервер хранит только его хэш, повторно узнать нельзя. */
export function BioAgentTokenDialog({
  open,
  onOpenChange,
  token,
  agentName,
}: BioAgentTokenDialogProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div>
            <DialogTitle>Токен агента «{agentName}»</DialogTitle>
            <DialogDescription>
              Показывается один раз — на сервере хранится только хэш.
            </DialogDescription>
          </div>
          <DialogClose onClose={() => onOpenChange(false)} />
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-md bg-muted p-3 font-mono text-sm break-all">
            {token}
          </div>
          <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Скопируйте сейчас. Если токен потеряется — его можно только
              перевыпустить, старый перестанет работать.
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="mr-2 h-4 w-4 text-green-600" />
                Скопировано
              </>
            ) : (
              <>
                <Copy className="mr-2 h-4 w-4" />
                Копировать токен
              </>
            )}
          </Button>
          <Button onClick={() => onOpenChange(false)}>Готово</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
